import { useEffect, useState } from 'react'
import {
  ClipboardList, Users, Loader2, Link2, RotateCw, Trash2, Copy, Check,
  ExternalLink, AlertTriangle, X, Save, Calendar, Clock, ArrowUp, ArrowDown,
  Sparkles,
} from 'lucide-react'
import { api } from '../lib/api.js'
import { CARD } from '../components/ui.jsx'
import { useConfirm } from '../components/ConfirmProvider.jsx'

/**
 * Patient list tab — sourced from the public registration survey.
 *
 *   1. Top card: registration link state (generate / copy / open / revoke)
 *      with a live response count.
 *   2. Bottom card: full table of registered patients.
 *
 *   The registration link is the same one managed in the Company tab's
 *   SurveyPanel (RegistrationCard) — both views read/write the same token
 *   on `operation.survey_registration_token`.
 */
export default function OperationPatientListTab({ op }) {
  const [summary, setSummary] = useState(null)
  const [rows,    setRows]    = useState(null)
  const [error,   setError]   = useState(null)
  const [editing, setEditing] = useState(null)    // service_registration row, or null

  function reload() {
    setError(null)
    Promise.all([
      api.operations.surveys.summary(op.id),
      api.operations.surveys.registrations(op.id),
    ])
      .then(([s, r]) => { setSummary(s); setRows(r) })
      .catch((e) => setError(e.message))
  }
  useEffect(reload, [op.id])      // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className={`${CARD} p-5 ring-rose-200/60 bg-rose-50/40 text-rose-700 text-[13px] inline-flex items-center gap-2`}>
        <AlertTriangle size={14} /> {error}
      </div>
    )
  }
  if (!summary || !rows) {
    return (
      <div className={`${CARD} p-10 text-center text-zinc-400`}>
        <Loader2 className="inline w-4 h-4 animate-spin mr-1.5" /> Loading…
      </div>
    )
  }

  const liveRow = editing ? (rows.find((r) => r.id === editing.id) || editing) : null

  return (
    <div className="space-y-4">
      <RegistrationLinkCard
        op={op}
        info={summary.registration}
        onChange={reload}
      />
      <PatientTableCard rows={rows} onRowClick={setEditing} />
      {liveRow && (
        <PatientDrawer
          op={op}
          row={liveRow}
          onClose={() => setEditing(null)}
          onChanged={reload}
          onDeleted={() => { setEditing(null); reload() }}
        />
      )}
    </div>
  )
}


/* ─────────── Registration link ─────────── */

function RegistrationLinkCard({ op, info, onChange }) {
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)
  // Whether the public registration page shows the "บริการที่ทำได้" services
  // card. Optimistic local mirror of info.show_services so the switch feels
  // instant; reconciled with the server response.
  const [showServices, setShowServices] = useState(!!info.show_services)
  const [svcBusy, setSvcBusy] = useState(false)
  useEffect(() => { setShowServices(!!info.show_services) }, [info.show_services])

  async function toggleServices() {
    const next = !showServices
    setShowServices(next)            // optimistic
    setSvcBusy(true)
    try {
      const res = await api.operations.surveys.setShowServices(op.id, next)
      setShowServices(!!res.show_services)
    } catch {
      setShowServices(!next)         // revert on failure
    } finally {
      setSvcBusy(false)
    }
  }
  // Once someone has registered, regenerating or revoking the link would
  // either break their saved URL or orphan their data. Both buttons are
  // hard-disabled in that state; the link itself is still usable.
  const locked = info.count > 0

  async function gen() {
    setBusy(true)
    try { await api.operations.surveys.generateRegistration(op.id); onChange() }
    finally   { setBusy(false) }
  }
  async function revoke() {
    if (locked) return
    if (!await confirm({
      title: 'ยกเลิกลิงก์ลงทะเบียน',
      message: 'ลิงก์ปัจจุบันจะใช้ไม่ได้อีก — คนที่ได้ลิงก์ไปแล้วจะเปิดไม่ได้',
      confirmLabel: 'ยกเลิกลิงก์',
      tone: 'warn',
    })) return
    setBusy(true)
    try { await api.operations.surveys.revokeRegistration(op.id); onChange() }
    finally   { setBusy(false) }
  }
  async function regen() {
    if (locked) return
    if (!await confirm({
      title: 'สร้างลิงก์ใหม่',
      message: 'ลิงก์เดิมจะใช้ไม่ได้อีก — คนที่ได้ลิงก์เก่าต้องขอลิงก์ใหม่',
      confirmLabel: 'สร้างใหม่',
      tone: 'warn',
    })) return
    setBusy(true)
    try {
      await api.operations.surveys.revokeRegistration(op.id)
      await api.operations.surveys.generateRegistration(op.id)
      onChange()
    } finally { setBusy(false) }
  }

  const lockedTitle = locked ? 'มีผู้ลงทะเบียนแล้ว — เปลี่ยนลิงก์ไม่ได้' : undefined

  return (
    <div className={`${CARD} p-5 md:p-6`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-zinc-900">
            <ClipboardList size={14} className="text-amber-500" /> ลิงก์ลงทะเบียน
          </h3>
          <p className="text-[12px] text-zinc-500 mt-0.5">HR ส่งให้พนักงานลงทะเบียน — รายชื่อจะมาแสดงในตารางด้านล่าง</p>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[24px] font-bold text-zinc-900 tabular-nums leading-none">{info.count}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">ลงทะเบียนแล้ว</div>
        </div>
      </div>

      {info.link ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 rounded-lg ring-1 ring-zinc-200 bg-zinc-50/60 px-2.5 py-1.5">
            <Link2 size={13} className="shrink-0 text-zinc-400" />
            <a href={info.link} target="_blank" rel="noreferrer"
              className="min-w-0 flex-1 truncate text-[12.5px] text-amber-700 hover:text-amber-800 hover:underline tabular-nums"
              title={info.link}>
              {info.link}
            </a>
            <CopyButton text={info.link} />
            <a href={info.link} target="_blank" rel="noreferrer"
              title="เปิดในแท็บใหม่"
              className="w-7 h-7 rounded-md text-zinc-500 hover:text-amber-700 hover:bg-amber-50 inline-flex items-center justify-center">
              <ExternalLink size={12} />
            </a>
          </div>
          {/* Toggle: show the "บริการที่ทำได้" services card on the public page */}
          <ServicesToggle on={showServices} busy={svcBusy} onToggle={toggleServices} />
        </div>
      ) : (
        // The link is auto-issued on op creation + by migration 033, so this
        // branch only renders after an explicit Revoke (and only when count
        // was 0, so we re-offer Generate without scaring anyone).
        <button onClick={gen} disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
          สร้างลิงก์ลงทะเบียน
        </button>
      )}

    </div>
  )
}

// On/off switch that controls whether the public registration page shows the
// "บริการที่ทำได้ในรถ Tooth Taxi" services card.
function ServicesToggle({ on, busy, onToggle }) {
  return (
    <div className="flex items-center justify-between gap-3 mt-1 px-3 py-2.5 rounded-lg bg-amber-50/50 ring-1 ring-amber-100">
      <div className="min-w-0">
        <div className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-zinc-800">
          <Sparkles size={13} className="text-amber-500" /> แสดงรายการบริการในหน้าลงทะเบียน
        </div>
        <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">
          การ์ด “บริการที่ทำได้ในรถ Tooth Taxi” (ขูดหินปูน อุดฟัน ถอนฟัน ฯลฯ)
        </p>
      </div>
      <button type="button" role="switch" aria-checked={on} disabled={busy} onClick={onToggle}
        title={on ? 'กำลังแสดง — กดเพื่อซ่อน' : 'ซ่อนอยู่ — กดเพื่อแสดง'}
        className={`relative shrink-0 w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-amber-500' : 'bg-zinc-300'}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : ''}`} />
      </button>
    </div>
  )
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    } catch {}
  }
  return (
    <button onClick={copy} title="คัดลอกลิงก์"
      className="w-7 h-7 rounded-md text-zinc-500 hover:text-amber-700 hover:bg-amber-50 inline-flex items-center justify-center">
      {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
    </button>
  )
}


/* ─────────── Patient table ─────────── */

function PatientTableCard({ rows, onRowClick }) {
  // Default: newest registrations first. Click a sortable column header to
  // toggle direction; click the other to switch column (starts at desc).
  const [sortKey, setSortKey] = useState('created_at')  // 'created_at' | 'slot'
  const [sortDir, setSortDir] = useState('desc')        // 'asc' | 'desc'

  function toggleSort(key) {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('desc') }
  }

  const sorted = [...rows].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1
    if (sortKey === 'created_at') return ((a.created_at || 0) - (b.created_at || 0)) * dir
    // 'slot' — compare by (slot_date, slot_time_period) tuple. Missing values
    // sort last so empty slots don't appear ahead of real ones in asc order.
    const ad = a.slot_date || '￿'
    const bd = b.slot_date || '￿'
    if (ad !== bd) return ad < bd ? -dir : dir
    const ap = a.slot_time_period || '￿'
    const bp = b.slot_time_period || '￿'
    return ap < bp ? -dir : ap > bp ? dir : 0
  })

  return (
    <div className={`${CARD} p-5 md:p-6`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-zinc-900">
            <Users size={14} className="text-amber-500" /> รายชื่อผู้ลงทะเบียน
          </h3>
          <p className="text-[12px] text-zinc-500 mt-0.5">คลิกแถวเพื่อแก้ไขข้อมูลหรือลบ · คลิกหัวคอลัมน์เพื่อจัดเรียง</p>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="py-10 text-center text-zinc-400 text-[12.5px]">
          ยังไม่มีผู้ลงทะเบียน — แชร์ลิงก์ด้านบนให้ HR
        </div>
      ) : (
        <div className="overflow-x-auto -mx-3 md:-mx-5">
          <table className="w-full text-[12.5px]">
            <thead className="bg-zinc-50/70 text-[10.5px] uppercase tracking-wider text-zinc-500">
              <tr>
                <SortableTh label="วันที่ลงทะเบียน"   active={sortKey === 'created_at'} dir={sortDir} onClick={() => toggleSort('created_at')} />
                <th className="px-3 md:px-5 py-2 text-left font-medium">ชื่อ-สกุล</th>
                <th className="px-3 md:px-5 py-2 text-left font-medium">โทร</th>
                <SortableTh label="ช่วงเวลาที่เลือก" active={sortKey === 'slot'}       dir={sortDir} onClick={() => toggleSort('slot')} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id}
                  onClick={() => onRowClick(r)}
                  className="border-b border-zinc-100 last:border-b-0 hover:bg-amber-50/40 cursor-pointer transition-colors">
                  <td className="px-3 md:px-5 py-1.5 text-zinc-500 tabular-nums">{fmtFull(r.created_at)}</td>
                  <td className="px-3 md:px-5 py-1.5 text-zinc-900">{r.fullname}</td>
                  <td className="px-3 md:px-5 py-1.5 text-zinc-700 tabular-nums">{r.phone}</td>
                  <td className="px-3 md:px-5 py-1.5 text-zinc-700">{slotLabelSplit(r) || slotLabel(r.time_slot)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function SortableTh({ label, active, dir, onClick }) {
  return (
    <th className="px-3 md:px-5 py-2 text-left font-medium">
      <button type="button" onClick={onClick}
        className={'inline-flex items-center gap-1 -mx-1 px-1 rounded hover:bg-zinc-100 transition ' +
          (active ? 'text-amber-700' : 'text-zinc-500 hover:text-zinc-900')}>
        {label}
        {active
          ? (dir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />)
          : <span className="w-[10px] inline-block opacity-30">⇅</span>}
      </button>
    </th>
  )
}


/* ─────────── Patient detail drawer (edit / delete) ─────────── */

function PatientDrawer({ op, row, onClose, onChanged, onDeleted }) {
  const confirm = useConfirm()
  const [fullname, setFullname] = useState(row.fullname || '')
  const [phone,    setPhone]    = useState(row.phone || '')
  const [email,    setEmail]    = useState(row.email || '')
  const [idcard,   setIdcard]   = useState(row.idcard || '')
  const [slotDate, setSlotDate] = useState(row.slot_date || '')
  const [slotPeriod, setSlotPeriod] = useState(row.slot_time_period || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [onClose])

  // Day list across the op's date range; time options = 1-hour windows within
  // the op's working hours. Falls back to whatever is currently saved so an
  // older row outside the new range still has a usable selection.
  const dayOptions  = useDayOptions(op?.start_date, op?.end_date, slotDate)
  const slotOptions = useSlotOptions(op?.start_time, op?.end_time, slotPeriod)

  const phoneOk  = /^\d{10}$/.test(phone.replace(/[^\d]/g, ''))
  const emailOk  = !email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const idcardOk = /^\d{13}$/.test(idcard.trim())
  const slotOk   = !!slotDate && /^\d{2}:\d{2}-\d{2}:\d{2}$/.test(slotPeriod)
  const slotChanged = slotDate !== (row.slot_date || '') || slotPeriod !== (row.slot_time_period || '')
  const dirty = fullname.trim() !== (row.fullname || '')
            || phone.replace(/[^\d]/g, '') !== (row.phone || '')
            || (email.trim() || null) !== (row.email || null)
            || idcard.trim() !== (row.idcard || '')
            || slotChanged

  async function save() {
    if (busy) return
    setErr(null)
    if (!fullname.trim()) { setErr('กรุณากรอกชื่อ-สกุล'); return }
    if (!phoneOk)         { setErr('เบอร์โทรต้อง 10 หลัก'); return }
    if (!emailOk)         { setErr('รูปแบบอีเมลไม่ถูกต้อง'); return }
    if (!idcardOk)        { setErr('เลขบัตรประชาชนต้อง 13 หลัก'); return }
    if (!slotOk)          { setErr('กรุณาเลือกวันและช่วงเวลา'); return }
    setBusy(true)
    try {
      const body = {
        fullname: fullname.trim(),
        phone:    phone.replace(/[^\d]/g, ''),
        email:    email.trim(),
        idcard:   idcard.trim(),
      }
      // Only send slot fields when actually changing them — saves a round-trip
      // of validation + time_slot rebuild on the backend for name-only edits.
      if (slotChanged) {
        body.slot_date        = slotDate
        body.slot_time_period = slotPeriod
      }
      await api.operations.surveys.updateRegistration(op.id, row.id, body)
      onChanged()
      onClose()
    } catch (e) { setErr(extractErr(e)) }
    finally     { setBusy(false) }
  }

  async function remove() {
    if (!await confirm({
      title: 'ลบรายชื่อผู้ลงทะเบียน',
      message: `"${row.fullname}" จะถูกลบออกถาวร — กู้คืนไม่ได้`,
      confirmLabel: 'ลบ',
      tone: 'danger',
    })) return
    setBusy(true); setErr(null)
    try {
      await api.operations.surveys.deleteRegistration(op.id, row.id)
      onDeleted()
    } catch (e) { setErr(extractErr(e)); setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-40">
      <div onClick={onClose} className="absolute inset-0 bg-zinc-900/30 backdrop-blur-[1px]" />
      <aside className="absolute right-0 top-0 h-full w-[480px] max-w-[100vw] bg-white shadow-2xl ring-1 ring-zinc-200 flex flex-col">
        <header className="px-5 py-4 border-b border-zinc-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-amber-700 font-semibold">รายชื่อผู้ลงทะเบียน</div>
            <h2 className="text-[16px] font-semibold text-zinc-900 truncate">{row.fullname}</h2>
            <p className="text-[11.5px] text-zinc-500 mt-0.5 tabular-nums">ลงทะเบียน {fmtFull(row.created_at)}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 inline-flex items-center justify-center">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <section className="space-y-2.5">
            <h3 className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">ข้อมูลผู้ลงทะเบียน</h3>
            <DrawerField label="ชื่อ-สกุล" value={fullname} onChange={setFullname} />
            <DrawerField label="เบอร์โทร" value={phone} onChange={setPhone} tabular maxLength={20} />
            <DrawerField label="อีเมล (ถ้ามี)" value={email} onChange={setEmail} type="email" />
            <DrawerField label="เลขบัตรประชาชน" value={idcard} onChange={setIdcard} tabular maxLength={13} />
          </section>

          <section>
            <h3 className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">ช่วงเวลาที่เลือก</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <label className="block">
                <span className="mb-1 text-[11.5px] text-zinc-600 font-medium inline-flex items-center gap-1.5">
                  <Calendar size={11} className="text-zinc-400" /> วันที่
                </span>
                <select value={slotDate} onChange={(e) => setSlotDate(e.target.value)}
                  className="w-full px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-[13px] bg-white">
                  {!slotDate && <option value="">— เลือก —</option>}
                  {dayOptions.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 text-[11.5px] text-zinc-600 font-medium inline-flex items-center gap-1.5">
                  <Clock size={11} className="text-zinc-400" /> ช่วงเวลา
                </span>
                <select value={slotPeriod} onChange={(e) => setSlotPeriod(e.target.value)}
                  className="w-full px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-[13px] bg-white tabular-nums">
                  {!slotPeriod && <option value="">— เลือก —</option>}
                  {slotOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          {err && (
            <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
          <button onClick={remove} disabled={busy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12.5px] text-rose-700 hover:bg-rose-50 ring-1 ring-rose-200 disabled:opacity-50">
            <Trash2 size={12} /> ลบรายชื่อ
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={busy}
              className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">
              ปิด
            </button>
            <button onClick={save} disabled={busy || !dirty}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} บันทึก
            </button>
          </div>
        </footer>
      </aside>
    </div>
  )
}

function DrawerField({ label, value, onChange, type = 'text', tabular, maxLength }) {
  return (
    <label className="block">
      <span className="block mb-1 text-[11.5px] text-zinc-600 font-medium">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} maxLength={maxLength}
        className={'w-full px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-[13px] ' +
          (tabular ? 'tabular-nums' : '')} />
    </label>
  )
}

function extractErr(e) {
  const m = (e?.message || '').match(/:\s*\{?"?error"?"?:\s*"?([^"}\n]+)/)
  return m ? m[1] : (e?.message || 'error')
}

// All days between op start/end (inclusive). If `current` is set but outside
// the range, prepend it so the existing selection stays visible — staff can
// re-pick from inside the range or keep the old one.
function useDayOptions(startDate, endDate, current) {
  if (!startDate || !endDate || endDate < startDate) {
    return current ? [{ value: current, label: thaiBeDate(current) }] : []
  }
  const out = []
  let cur = startDate
  for (let i = 0; cur <= endDate && i < 31; i++) {
    out.push({ value: cur, label: thaiBeDate(cur) })
    cur = nextDay(cur)
  }
  if (current && !out.some((d) => d.value === current)) {
    out.unshift({ value: current, label: `${thaiBeDate(current)} (เดิม)` })
  }
  return out
}

// 1-hour windows within op working hours. Falls back to a sensible 9-17
// grid when the op times are missing, and always prepends the stored value
// (so legacy/half-hour slots remain selectable for old rows).
function useSlotOptions(startTime, endTime, current) {
  const [sh, sm] = parseHm(startTime, 9, 0)
  const [eh, em] = parseHm(endTime, 17, 0)
  const startMin = sh * 60 + sm
  const endMin   = eh * 60 + em
  const out = []
  if (endMin > startMin) {
    for (let m = startMin; m + 60 <= endMin; m += 60) {
      out.push(`${pad2(Math.floor(m / 60))}:${pad2(m % 60)}-${pad2(Math.floor((m + 60) / 60))}:${pad2((m + 60) % 60)}`)
    }
  }
  if (current && !out.includes(current)) out.unshift(current)
  return out
}

function parseHm(s, dh, dm) {
  if (typeof s !== 'string') return [dh, dm]
  const m = s.match(/^(\d{1,2}):(\d{2})$/)
  return m ? [Number(m[1]), Number(m[2])] : [dh, dm]
}
function pad2(n) { return String(n).padStart(2, '0') }
function nextDay(ymd) {
  const t = Date.parse(`${ymd}T12:00:00Z`)
  const d = new Date(t + 24 * 3600 * 1000)
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`
}
function thaiBeDate(ymd) {
  const [y, m, d] = ymd.split('-').map(Number)
  try {
    return new Date(Date.UTC(y, m - 1, d))
      .toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
  } catch { return ymd }
}


/* ─────────── helpers ─────────── */

function slotLabel(value) {
  if (!value) return '—'
  if (!value.includes('|')) return value
  const [a, b] = value.split('|')
  try {
    const da = new Date(a); const db = new Date(b)
    return `${da.toLocaleString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} – ${db.toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })}`
  } catch {
    return value
  }
}
// Prefer the split columns when present (post-migration 032). Format:
// "1 มิ.ย. 2569 · 09:00-10:00". Returns null when split fields aren't set yet.
function slotLabelSplit(r) {
  if (!r?.slot_date || !r?.slot_time_period) return null
  const [y, m, d] = r.slot_date.split('-').map(Number)
  try {
    const date = new Date(Date.UTC(y, m - 1, d))
      .toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
    return `${date} · ${r.slot_time_period}`
  } catch { return `${r.slot_date} · ${r.slot_time_period}` }
}

function fmtFull(unix) {
  if (!unix) return '—'
  const d = new Date(unix * 1000)
  return d.toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })
}
